import { Swiper, SwiperSlide } from 'swiper/react';
import EcoSpace from 'eco-design/base/eco-space';
import { useState, useRef } from 'react';
import EcoImg from 'eco-design/base/eco-img';
import styles from './index.module.scss';
import { useLang } from '@/app/utils/useLang';
import getLocal from 'eco-design-reliance/getLocal';
import CommonTitle from '../../components/CommonTitle';
import AnimatedSection from '../../components/AnimatedSection';
import Tabs from '../../components/Tabs';
import { icons, images } from './img';

type ThingItem = {
    text: string;
    text2: string;
    icon: string;
};

type OutputTab = {
    tabLabel: any;
    tabValue: string;
    pc: string;
    pad: string;
    h5: string;
    things: ThingItem[];
};

const Output: React.FC = () => {
    const { t } = useLang('Output');
    const swiperRef = useRef<any>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const { locale = 'us' } = getLocal();
    const isEu = ['eu', 'uk', 'de', 'fr', 'es', 'it', 'nl'].includes(locale);


    const getThingCopy = (tabIndex: number, thingIndex: number): Omit<ThingItem, 'icon'> => {
        const baseKey = `tab${tabIndex}_thing${thingIndex}`;

        try {
            return {
                text: t(`${baseKey}_1`),
                text2: t(`${baseKey}_2`),
            };
        } catch {
            return {
                text: t(baseKey),
                text2: '',
            };
        }
    };

    const tabs: OutputTab[] = [
        {
            tabLabel: t('tab1'),
            tabValue: 'a1',
            pc: images[locale][0].pc,
            pad: images[locale][0].pc,
            h5: images[locale][0].h5,
            things: [
                {
                    ...getThingCopy(1, 1),
                    icon: icons[locale][0][0],
                },
                {
                    ...getThingCopy(1, 2),
                    icon: icons[locale][0][1],
                },
                {
                    ...getThingCopy(1, 3),
                    icon: icons[locale][0][2],
                },
            ],
        },
        {
            tabLabel: t('tab2'),
            tabValue: 'a2',
            pc: images[locale][1].pc,
            pad: images[locale][1].pc,
            h5: images[locale][1].h5,
            things: [
                {
                    ...getThingCopy(2, 1),
                    icon: icons[locale][1][0],
                },
                {
                    ...getThingCopy(2, 2),
                    icon: icons[locale][1][1],
                },
                {
                    ...getThingCopy(2, 3),
                    icon: icons[locale][1][2],
                },
            ],
        },
        {
            tabLabel: t('tab3'),
            tabValue: 'a3',
            pc: images[locale][2].pc,
            pad: images[locale][2].pc,
            h5: images[locale][2].h5,
            things: [
                {
                    ...getThingCopy(3, 1),
                    icon: icons[locale][2][0],
                },
                {
                    ...getThingCopy(3, 2),
                    icon: icons[locale][2][1],
                },
                {
                    ...getThingCopy(3, 3),
                    icon: icons[locale][2][2],
                },
            ],
        },
        (locale === 'de' || locale === 'fr') && {
            tabLabel: t('tab4'),
            tabValue: 'a4',
            pc: images[locale][3].pc,
            pad: images[locale][3].pc,
            h5: images[locale][3].h5,
            things: [
                {
                    ...getThingCopy(4, 1),
                    icon: icons[locale][3][0],
                },
                {
                    ...getThingCopy(4, 2),
                    icon: icons[locale][3][1],
                },
                {
                    ...getThingCopy(4, 3),
                    icon: icons[locale][3][2],
                },
            ],
        },
    ].filter(Boolean) as OutputTab[];

    const onChange = (_item: any, index: number) => {
        setActiveIndex(index);
        swiperRef.current.slideTo(index);
    };

    const handleSlideChange = (swiper: any) => {
        const index = swiper.realIndex; //下标
        setActiveIndex(index);

        // tab 滚动
        const targetTabItem = document.querySelectorAll('.output-tabs .eco-tab-item')[index];
        const tabsEle = document.querySelector('.output-tabs .eco-tab-items-move');

        if (!targetTabItem || !tabsEle) return;
        const parentRect = targetTabItem.getBoundingClientRect();

        const elemRect = tabsEle.getBoundingClientRect();

        let scrollLeft: number;

        const padding = window.innerWidth > 1024 ? 0 : window.innerWidth > 768 ? 48 : 24;
        // 如果是第一个item，滚动到最左边
        if (index === 0) {
            scrollLeft = elemRect.left - parentRect.left - padding;
        }
        // 如果是最后一个item，滚动到最右边
        else if (index === tabs.length - 1) {
            scrollLeft = elemRect.right - parentRect.right + padding;
        }
        // 其他情况居中显示
        else {
            scrollLeft = elemRect.left - parentRect.left - parentRect.width / 2 + elemRect.width / 2;
        }

        parent.scrollBy({
            left: scrollLeft,
            behavior: 'smooth',
        });
    };

    return (
        <div className={`${styles.Output} range2  GtmModuleData-section`} data-section-id="d3c-Output" id="Output">
            {!isEu && <EcoSpace />}
            <CommonTitle
                title={t('title')}
                desc={t.rich('desc', {
                    sup: (text: string) => <sup>{text}</sup>,
                })}
            />
            <AnimatedSection variant="fadeInUp" options={{ delay: 400 }}>
                <div className="swiper-output d3m-layout">
                    <Swiper
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        slidesPerView={1}
                        spaceBetween={12}
                        initialSlide={0}
                        onSlideChange={handleSlideChange}
                    >
                        {tabs.map((item, index) => (
                            <SwiperSlide key={index} className={`list-item list-item-${index + 1}`}>
                                <EcoImg pc={item.pc} pad={item.pad} h5={item.h5} isOpenLazy={true} />
                                <div className="things">
                                    {item.things.map((thing, _index) => (
                                        <div key={_index} className="thing">
                                            <div className="thing-icon">
                                                {/* <img src={thing} alt="" /> */}
                                                <EcoImg className='icon-img' pc={thing.icon} isOpenLazy={true} />
                                            </div>
                                            <div className="thing-content">
                                                <div className="thing-label">{thing.text}</div>
                                                {!!thing.text2 && <div className="thing-value">{thing.text2}</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </AnimatedSection>

            <div className="d3m-tabs">
                <Tabs tabs={tabs} activeIndex={activeIndex} onChange={onChange} />
            </div>
        </div>
    );
};

export default Output;
